import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import Draggable from "react-draggable";
import "bootstrap/dist/css/bootstrap.min.css";
import "./Chat.css";

const Chat = ({ trainerID, isChatOpen, closeChat }) => {
	const [threads, setThreads] = useState([]);
	const [selectedThread, setSelectedThread] = useState(null);
	const [messages, setMessages] = useState([]);
	const [newMessage, setNewMessage] = useState("");
	const [isNewChat, setIsNewChat] = useState(false);
	const [searchQuery, setSearchQuery] = useState("");
	const [users, setUsers] = useState([]);
	const [filteredUsers, setFilteredUsers] = useState([]);
	const [unreadMessages, setUnreadMessages] = useState({});
	const [loading, setLoading] = useState(false);
	const wsRef = useRef(null);
	const messagesEndRef = useRef(null);

	const WS_URL = process.env.REACT_APP_WS_URL;

	// ✅ Load last opened thread
	useEffect(() => {
		const lastThreadId = localStorage.getItem("lastThreadId");

		// ✅ Ensure it only runs if threads are already loaded
		if (lastThreadId && threads.length > 0 && !selectedThread) {
			const thread = threads.find((t) => t.threadId === lastThreadId);
			if (thread) openThread(thread);
		}
	}, [threads]); // ✅ Only re-run if thread list updates

	// ✅ WebSocket Connection
	useEffect(() => {
		if (trainerID && WS_URL) {
			if (!wsRef.current) {
				wsRef.current = new WebSocket(WS_URL);

				wsRef.current.onopen = () => {
					console.log("🔗 WebSocket connected");
					wsRef.current.send(
						JSON.stringify({ type: "register", TrainerID: trainerID })
					);
				};

				wsRef.current.onmessage = (event) => {
					const data = JSON.parse(event.data);
					console.log("📩 WebSocket Message Received:", data);

					setMessages((prevMessages) => {
						// Ensure state update
						const updatedMessages = [...prevMessages, data];
						return updatedMessages;
					});
				};

				wsRef.current.onerror = (error) => {
					console.error("❌ WebSocket error:", error);
				};

				wsRef.current.onclose = () => {
					console.log("🔌 WebSocket disconnected, attempting to reconnect...");
					wsRef.current = null;
					setTimeout(() => {
						if (!wsRef.current) {
							wsRef.current = new WebSocket(WS_URL);
						}
					}, 5000); // Retry connection after 5 seconds
				};
			}
		}

		return () => {
			if (wsRef.current) {
				wsRef.current.close();
				wsRef.current = null;
			}
		};
	}, [trainerID, WS_URL]);

	// ✅ Fetch Threads
	useEffect(() => {
		let isMounted = true; // ✅ Prevent re-renders from triggering new fetches

		const fetchThreads = async () => {
			try {
				setLoading(true);
				console.log("Fetching threads for trainerID:", trainerID);

				const response = await axios.get(
					`${process.env.REACT_APP_API_URL}/chat/list-threads/${trainerID}`
				);

				if (
					isMounted &&
					Array.isArray(response.data?.threads) &&
					response.data.threads.length > 0
				) {
					setThreads(response.data.threads);

					// ✅ Dynamically update unread messages
					const unreadMap = {};
					response.data.threads.forEach((thread) => {
						if (thread.hasUnread) {
							unreadMap[thread.threadId] = true;
						}
					});
					setUnreadMessages(unreadMap);
				} else if (isMounted) {
					setThreads([]);
				}
			} catch (error) {
				console.error("🚨 Error fetching threads:", error);
			} finally {
				setLoading(false);
			}
		};

		if (isChatOpen && trainerID) {
			fetchThreads();
		}

		return () => {
			isMounted = false; // ✅ Prevent unnecessary state updates
		};
	}, [trainerID, isChatOpen]); // ✅ Removed `messages` from dependencies

	// ✅ Open a Thread & Fetch Messages
	const openThread = async (thread) => {
		console.log("📂 Opening thread:", thread.threadId);
		setSelectedThread(thread);
		localStorage.setItem("lastThreadId", thread.threadId);

		try {
			const response = await axios.get(
				`${process.env.REACT_APP_API_URL}/chat/get-messages/${thread.threadId}`
			);
			setMessages(response.data.messages);
		} catch (error) {
			console.error("🚨 Error fetching messages:", error);
		}

		// ✅ Instantly mark messages as read without requiring a refresh
		await axios.post(
			`${process.env.REACT_APP_API_URL}/chat/mark-messages-read`,
			{
				threadId: thread.threadId,
				user: trainerID,
			}
		);

		setUnreadMessages((prev) => ({
			...prev,
			[thread.threadId]: false,
		}));
	};

	useEffect(() => {
		messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
	}, [messages]); // Scrolls when messages update

	// ✅ Fetch Users for New Chat
	useEffect(() => {
		const fetchUsers = async () => {
			try {
				const response = await axios.get(
					`${process.env.REACT_APP_API_URL}/chat/get-users`
				);

				if (response.data && Array.isArray(response.data)) {
					console.log("✅ Users fetched:", response.data);
					setUsers(response.data);
					setFilteredUsers(response.data); // ✅ Initialize filteredUsers with full list
				} else {
					console.error("🚨 Unexpected users response:", response.data);
					setUsers([]);
					setFilteredUsers([]);
				}
			} catch (error) {
				console.error("🚨 Error fetching users:", error);
			}
		};

		if (isNewChat) {
			fetchUsers();
		}
	}, [isNewChat]);

	// ✅ Start a New Chat
	const startNewChat = async (user) => {
		setIsNewChat(false);
		const existingThread = threads.find(
			(thread) =>
				thread.participants.includes(trainerID) &&
				thread.participants.includes(user.TrainerID)
		);

		if (existingThread) {
			openThread(existingThread);
		} else {
			createNewThread(user);
		}
	};

	// ✅ Create a New Thread
	const createNewThread = async (user) => {
		try {
			const response = await axios.post(
				`${process.env.REACT_APP_API_URL}/chat/create-direct-thread`,
				{
					sender: trainerID,
					recipient: user.TrainerID,
				}
			);

			if (response.data?.threadId) {
				const newThread = {
					threadId: response.data.threadId,
					participants: [trainerID, user.TrainerID],
					messages: [],
				};

				setThreads((prev) => [...prev, newThread]);
				openThread(newThread);
			}
		} catch (error) {
			console.error("🚨 Error starting new chat:", error);
		}
	};

	// ✅ Send Message & Update Chat
	const sendMessage = async () => {
		if (!newMessage.trim()) return;

		const newMsg = {
			sender: trainerID,
			message: newMessage,
			timestamp: new Date().toISOString(),
			readBy: [trainerID],
		};

		setMessages((prevMessages) => [...prevMessages, newMsg]);
		setNewMessage("");

		try {
			await axios.post(`${process.env.REACT_APP_API_URL}/chat/send-message`, {
				threadId: selectedThread.threadId,
				sender: trainerID,
				message: newMessage,
			});
		} catch (error) {
			console.error("🚨 Error sending message:", error);
		}
	};

	// ✅ Back to Threads
	const goBackToThreads = () => {
		console.log("🔙 Going back to thread list...");

		// ✅ Force a state update to ensure rerender
		setSelectedThread(null);
		setMessages([]);
		setNewMessage("");
		setIsNewChat(false);
		localStorage.removeItem("lastThreadId");

		// ✅ Ensures re-render by forcing a state refresh
		setThreads([...threads]);
	};

	return (
		<Draggable handle=".chat-header" bounds="body">
			<div className={`chat-container ${isChatOpen ? "show" : "d-none"}`}>
				<div className="chat-header">
					<div className="modal-header bg-primary text-white d-flex align-items-center">
						{/* ✅ Chat Title (Centered) */}
						<h5 className="chat-title">Chat</h5>

						{/* ✅ Back & Close Buttons (Move to Right) */}
						<div className="right-buttons">
							{selectedThread ? (
								<button
									className="btn btn-light btn-sm back-btn"
									onClick={goBackToThreads}
								>
									🔙 Back
								</button>
							) : (
								<button
									className="btn btn-light btn-sm close-chat-btn"
									onClick={closeChat}
								>
									✖
								</button>
							)}
						</div>
					</div>
				</div>

				<div className="messages">
					{/* ✅ If Starting a New Chat, Show User Search */}
					{isNewChat ? (
						<div>
							<input
								type="text"
								className="form-control search-input"
								placeholder="Search for a user..."
								value={searchQuery}
								onChange={(e) => {
									const query = e.target.value.toLowerCase();
									setSearchQuery(query);

									// ✅ Ensure user.name exists before filtering
									setFilteredUsers(
										users.filter(
											(user) =>
												user?.user_name &&
												user.user_name.toLowerCase().includes(query)
										)
									);
								}}
							/>

							<ul className="list-group user-list mt-2">
								{filteredUsers.length === 0 ? (
									<p className="text-center text-muted">No users found</p>
								) : (
									filteredUsers.map((user, index) => (
										<li
											key={index}
											className="list-group-item list-group-item-action"
											onClick={() => startNewChat(user)}
										>
											{user.user_name || "Unnamed User"}
										</li>
									))
								)}
							</ul>
						</div>
					) : selectedThread ? (
						/* ✅ Render Messages if a Thread is Selected */
						messages.length > 0 ? (
							messages.map((msg, index) => (
								<div
									key={index}
									className={`message ${
										msg.sender === trainerID ? "sent" : "received"
									}`}
								>
									<p>{msg.message}</p>
									<small>{new Date(msg.timestamp).toLocaleTimeString()}</small>
								</div>
							))
						) : (
							<p className="text-muted text-center">No messages yet.</p>
						)
					) : (
						/* ✅ Show thread list when no thread is selected */
						<ul className="list-group thread-list">
							{threads.map((thread, index) => (
								<li
									key={index}
									className="list-group-item list-group-item-action mb-2"
									onClick={() => openThread(thread)}
								>
									<div className="d-flex align-items-center">
										{unreadMessages[thread.threadId] ? (
											<span className="new-badge">New</span>
										) : (
											<span className="read-badge">Read</span>
										)}
										<span>
											Chat with{" "}
											{thread.participants
												.filter((p) => p !== trainerID)
												.join(", ")}
										</span>
									</div>
								</li>
							))}
						</ul>
					)}
				</div>

				{/* ✅ Message Input: Only Show When a Thread is Selected */}
				{selectedThread && (
					<div className="input-group">
						<input
							type="text"
							className="message-input"
							placeholder="Type a message..."
							value={newMessage}
							onChange={(e) => setNewMessage(e.target.value)}
						/>
						<button className="btn btn-primary" onClick={sendMessage}>
							Send
						</button>
					</div>
				)}

				{/* ✅ New Message Button (At Bottom) */}
				{!selectedThread && (
					<button
						className="btn btn-success w-100 mb-3"
						onClick={() => setIsNewChat(true)}
					>
						➕ New Message
					</button>
				)}

				{/* ✅ Cancel Button when Searching for New Chat */}
				{isNewChat && (
					<button
						className="btn btn-danger w-100 mt-2"
						onClick={() => {
							setIsNewChat(false);
							setSearchQuery("");
						}}
					>
						Cancel
					</button>
				)}
			</div>
		</Draggable>
	);
};

export default Chat;
